import {
  buildOrgxApiUrl,
  resolveOrgxAuth,
  type ResolveOrgxAuthOptions,
} from "./auth.js";
import type { OrgxWorkspace } from "./workspaces.js";
import {
  getDailyBriefOnboardingDecision,
  recordDailyBriefOnboardingDecision,
} from "./wizard-state.js";

/**
 * Pull a short, user-readable hint out of an HTTP error body. Handles the
 * three shapes the orgx backend emits:
 *   { error: "string" }
 *   { error: { code?, message? } }
 *   { error: "...", details: { fieldErrors?: {...} } }
 * Falls back to null if nothing useful is present (caller uses `HTTP NNN`).
 */
function extractErrorHint(body: string): string | null {
  if (!body) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return body.slice(0, 160);
  }
  if (!parsed || typeof parsed !== "object") return null;
  const obj = parsed as Record<string, unknown>;
  const err = obj.error;
  if (typeof err === "string") return err;
  if (err && typeof err === "object") {
    const message = (err as Record<string, unknown>).message;
    if (typeof message === "string") return message;
    const code = (err as Record<string, unknown>).code;
    if (typeof code === "string") return code;
  }
  return null;
}

/**
 * Daily Brief + Trust Loop — wizard onboarding capture.
 *
 * Mirrors the web OnboardingCaptureScreen component at four steps:
 *   Step A — agent roster preview (defaults, no customization in CLI for v1)
 *   Step B — baseline prompts for task-type minutes
 *   Step C — optional weekly time-saved goal
 *   Step D — send-time preference (local time + timezone auto-detected)
 *
 * Commits atomically via POST /api/v1/onboarding/daily-brief.
 *
 * Gated on GET /api/v1/onboarding/daily-brief returning
 * workspaces[n].onboardingCompletedAt IS NULL — existing paired users
 * (v0.1.16 fast-path) skip unless they've never completed the capture.
 */

export interface DailyBriefOnboardingPrompts {
  cancel(message: string): void;
  isCancel(value: unknown): boolean;
  text(input: {
    initialValue?: string | undefined;
    message: string;
    placeholder?: string;
    validate?: (value: string | undefined) => Error | string | undefined;
  }): Promise<string | symbol>;
  select(input: {
    initialValue?: string | undefined;
    message: string;
    options: { label: string; value: string; hint?: string }[];
  }): Promise<string | symbol>;
  confirm(input: { message: string; initialValue?: boolean }): Promise<boolean | symbol>;
}

export interface DailyBriefOnboardingOptions {
  authOptions?: ResolveOrgxAuthOptions;
  force?: boolean;
  interactive: boolean;
  skip?: boolean;
  statePath?: string;
  workspace: OrgxWorkspace | null;
  prompts: DailyBriefOnboardingPrompts;
}

export interface DailyBriefOnboardingResult {
  status:
    | "completed"
    | "skipped_non_interactive"
    | "skipped_already_completed"
    | "skipped_no_workspace"
    | "skipped_user"
    | "cancelled"
    | "failed";
  message: string;
  previewUrl?: string;
  error?: string;
}

interface BaselinePrompt {
  task_type: string;
  label: string;
  placeholder: string;
}

const BASELINE_PROMPTS: BaselinePrompt[] = [
  { task_type: "code_review", label: "Code review", placeholder: "20" },
  { task_type: "prd_draft", label: "PRD draft", placeholder: "45" },
  { task_type: "test_writeup", label: "Test writeup", placeholder: "25" },
  { task_type: "pr_description", label: "PR description", placeholder: "10" },
  { task_type: "launch_post", label: "Launch post", placeholder: "60" },
  { task_type: "architecture_doc", label: "Architecture doc", placeholder: "90" },
];

const DAILY_BRIEF_SETUP_CHOICES = {
  defaults: "defaults",
  customize: "customize",
  skip: "skip",
} as const;

function buildDefaultBaselines(): { task_type: string; minutes: number; confidence: number }[] {
  return BASELINE_PROMPTS.map((bp) => ({
    task_type: bp.task_type,
    minutes: Number.parseInt(bp.placeholder, 10),
    confidence: 0.4,
  }));
}

function buildDefaultGoals(): {
  goal_type: "time_saved_weekly";
  target_value: number;
  unit: string;
  period: "weekly";
}[] {
  return [
    {
      goal_type: "time_saved_weekly",
      target_value: 180,
      unit: "minutes",
      period: "weekly",
    },
  ];
}

function normalizeSendTime(value: string): string {
  return value.trim().length === 4 ? `0${value.trim()}` : value.trim();
}

function recordSkipped(
  workspace: OrgxWorkspace,
  statePath?: string,
): DailyBriefOnboardingResult {
  recordDailyBriefOnboardingDecision(
    {
      workspaceId: workspace.id,
      workspaceName: workspace.name,
      status: "skipped",
      skippedAt: new Date().toISOString(),
    },
    statePath,
  );
  return {
    status: "skipped_user",
    message: "Daily Brief setup skipped. Run `wizard setup --daily-brief` when ready.",
  };
}

export async function runDailyBriefOnboarding(
  options: DailyBriefOnboardingOptions,
): Promise<DailyBriefOnboardingResult> {
  if (!options.workspace) {
    return {
      status: "skipped_no_workspace",
      message: "Daily Brief onboarding skipped — no workspace resolved.",
    };
  }

  if (options.skip) {
    return recordSkipped(options.workspace, options.statePath);
  }

  const localDecision = getDailyBriefOnboardingDecision(
    options.workspace.id,
    options.statePath,
  );
  if (!options.force && localDecision) {
    return {
      status: "skipped_already_completed",
      message:
        localDecision.status === "completed"
          ? "Daily Brief onboarding already completed for this workspace."
          : "Daily Brief setup was skipped for this workspace. Run `wizard setup --daily-brief` to configure.",
    };
  }

  if (!options.interactive) {
    return {
      status: "skipped_non_interactive",
      message: "Daily Brief onboarding skipped — not attached to a TTY.",
    };
  }

  const auth = await resolveOrgxAuth(options.authOptions);
  if (!auth) {
    return {
      status: "failed",
      message: "Daily Brief onboarding needs OrgX auth.",
      error: "no_auth",
    };
  }

  // Gate: is this workspace already onboarded?
  const state = await fetchOnboardingState(auth).catch(() => null);
  if (state) {
    const row = state.workspaces.find((w) => w.id === options.workspace!.id);
    if (row?.onboardingCompletedAt) {
      recordDailyBriefOnboardingDecision(
        {
          workspaceId: options.workspace.id,
          workspaceName: options.workspace.name,
          status: "completed",
          completedAt: row.onboardingCompletedAt,
        },
        options.statePath,
      );
      return {
        status: "skipped_already_completed",
        message: "Daily Brief onboarding already completed for this workspace.",
      };
    }
  }

  const { prompts } = options;

  const setupChoice = await prompts.select({
    message: "Set up Daily Brief now?",
    initialValue: DAILY_BRIEF_SETUP_CHOICES.defaults,
    options: [
      {
        value: DAILY_BRIEF_SETUP_CHOICES.defaults,
        label: "Use defaults",
        hint: "20m code review, 45m PRD, 07:00 daily.",
      },
      {
        value: DAILY_BRIEF_SETUP_CHOICES.customize,
        label: "Customize baselines",
        hint: "Answer baseline and delivery questions.",
      },
      {
        value: DAILY_BRIEF_SETUP_CHOICES.skip,
        label: "Skip and remember",
        hint: "Do not ask again unless you pass --daily-brief.",
      },
    ],
  });
  if (prompts.isCancel(setupChoice)) {
    return { status: "cancelled", message: "Daily Brief onboarding cancelled." };
  }
  if (setupChoice === DAILY_BRIEF_SETUP_CHOICES.skip) {
    return recordSkipped(options.workspace, options.statePath);
  }

  const shouldCustomize = setupChoice === DAILY_BRIEF_SETUP_CHOICES.customize;

  const baselines: { task_type: string; minutes: number; confidence: number }[] =
    shouldCustomize ? [] : buildDefaultBaselines();
  if (shouldCustomize) {
    for (const bp of BASELINE_PROMPTS) {
      const answer = await prompts.text({
        message: `${bp.label} minutes`,
        placeholder: bp.placeholder,
        validate(value) {
          if (!value || value.trim() === "") return undefined;
          const n = Number.parseInt(value, 10);
          if (!Number.isFinite(n) || n <= 0 || n > 24 * 60) {
            return "Enter 1-1440 or leave blank.";
          }
          return undefined;
        },
      });
      if (prompts.isCancel(answer)) {
        return { status: "cancelled", message: "Daily Brief onboarding cancelled." };
      }
      if (typeof answer === "string" && answer.trim() !== "") {
        const minutes = Number.parseInt(answer, 10);
        if (Number.isFinite(minutes) && minutes > 0) {
          baselines.push({ task_type: bp.task_type, minutes, confidence: 0.5 });
        }
      }
    }
  }

  const goals: {
    goal_type: "time_saved_weekly";
    target_value: number;
    unit: string;
    period: "weekly";
  }[] = shouldCustomize ? [] : buildDefaultGoals();
  if (shouldCustomize) {
    const goalAnswer = await prompts.text({
      message: "Weekly time-saved target minutes",
      placeholder: "180",
      validate(value) {
        if (!value || value.trim() === "") return undefined;
        const n = Number.parseInt(value, 10);
        if (!Number.isFinite(n) || n <= 0) {
          return "Enter a positive number or leave blank.";
        }
        return undefined;
      },
    });
    if (prompts.isCancel(goalAnswer)) {
      return { status: "cancelled", message: "Daily Brief onboarding cancelled." };
    }
    if (typeof goalAnswer === "string" && goalAnswer.trim() !== "") {
      const target = Number.parseInt(goalAnswer, 10);
      if (Number.isFinite(target) && target > 0) {
        goals.push({
          goal_type: "time_saved_weekly",
          target_value: target,
          unit: "minutes",
          period: "weekly",
        });
      }
    }
  }

  let sendTimeLocal = "07:00";
  let onlyOnAttention = false;
  if (shouldCustomize) {
    const sendTimeAnswer = await prompts.text({
      message: "Daily brief time (HH:MM)",
      placeholder: "07:00",
      validate(value) {
        if (!value || value.trim() === "") return undefined;
        if (!/^\d{1,2}:\d{2}$/.test(value.trim())) {
          return "Use HH:MM (24h).";
        }
        return undefined;
      },
    });
    if (prompts.isCancel(sendTimeAnswer)) {
      return { status: "cancelled", message: "Daily Brief onboarding cancelled." };
    }
    sendTimeLocal =
      typeof sendTimeAnswer === "string" && sendTimeAnswer.trim()
        ? normalizeSendTime(sendTimeAnswer)
        : "07:00";

    const onlyOnAttentionAnswer = await prompts.confirm({
      message: "Email only when attention is needed?",
      initialValue: false,
    });
    if (prompts.isCancel(onlyOnAttentionAnswer)) {
      return { status: "cancelled", message: "Daily Brief onboarding cancelled." };
    }
    onlyOnAttention = Boolean(onlyOnAttentionAnswer);
  }

  // Commit
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const commitPayload = {
    workspace_id: options.workspace.id,
    agent_roster: [
      { id: "engineering-agent", display_name: "Eli", color: "6, 182, 212", enabled: true },
      { id: "product-agent", display_name: "Pace", color: "22, 163, 74", enabled: true },
      { id: "marketing-agent", display_name: "Mark", color: "249, 115, 22", enabled: true },
      { id: "orchestrator-agent", display_name: "Xandy", color: "20, 184, 166", enabled: true },
    ],
    baselines,
    goals,
    starter_skill_loadouts: [],
    notification_preference: {
      channel: "email" as const,
      digest_kind: "daily_brief" as const,
      enabled: true,
      send_time_local: `${sendTimeLocal}:00`,
      timezone,
      only_on_attention: onlyOnAttention,
    },
  };

  const commitResponse = await fetch(buildOrgxApiUrl("/v1/onboarding/daily-brief", auth.baseUrl), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${auth.apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(commitPayload),
    signal: AbortSignal.timeout(10_000),
  });

  if (!commitResponse.ok) {
    const text = await commitResponse.text().catch(() => "");
    const hint = extractErrorHint(text) ?? `HTTP ${commitResponse.status}`;
    return {
      status: "failed",
      message: `Could not commit onboarding capture — ${hint}`,
      error: `HTTP ${commitResponse.status}: ${text.slice(0, 200)}`,
    };
  }

  const commitBody = (await commitResponse.json().catch(() => ({}))) as {
    daily_brief_preview_url?: unknown;
  };
  const previewUrl =
    typeof commitBody.daily_brief_preview_url === "string"
      ? commitBody.daily_brief_preview_url
      : "/today?mode=preview";
  recordDailyBriefOnboardingDecision(
    {
      workspaceId: options.workspace.id,
      workspaceName: options.workspace.name,
      status: "completed",
      completedAt: new Date().toISOString(),
    },
    options.statePath,
  );
  return {
    status: "completed",
    message: "Your Daily Brief is configured. First brief lands tomorrow.",
    previewUrl,
  };
}

interface OnboardingStateResponse {
  ok: boolean;
  workspaces: Array<{
    id: string;
    name: string;
    onboardingCompletedAt: string | null;
  }>;
}

async function fetchOnboardingState(
  auth: { apiKey: string; baseUrl: string },
): Promise<OnboardingStateResponse | null> {
  try {
    const res = await fetch(
      buildOrgxApiUrl("/v1/onboarding/daily-brief", auth.baseUrl),
      {
        method: "GET",
        headers: { Authorization: `Bearer ${auth.apiKey}` },
        signal: AbortSignal.timeout(5_000),
      },
    );
    if (!res.ok) return null;
    const body = (await res.json()) as OnboardingStateResponse;
    if (!body || !Array.isArray(body.workspaces)) return null;
    return body;
  } catch {
    return null;
  }
}
